/**
 * History Store — 歷史紀錄頁的時間範圍 + 面板狀態 + 紀錄列表
 *
 * 職責：
 * - 持有 preset (today / last7 / custom) 與 start_time / end_time（Unix 秒）
 * - 控制 HistoryHeader 三段式面板顯示狀態（list / selector / custom）
 * - 持有紀錄列表 records / total / totalBet / totalWin / 分頁狀態
 * - 提供 fetchRecords / loadMore action（內部從遊戲 config 抓 gameCode，呼叫端不用每次傳）
 *
 * 使用方式：
 *   const historyStore = useHistoryStore()
 *   historyStore.setGameId('hbfy')           // mount 時呼叫一次（gameCode 自動從 registry 抓）
 *   historyStore.fetchRecords()              // 首次載入
 *   // 之後使用者按 今天 / 最近 7 天 / 自定 確定 都會在 action 內部自動 fetch
 *   // 觸底用 historyStore.loadMore()
 *
 * 注意：
 * - 預設值是「今天」（00:00:00 ~ 23:59:59）
 * - 時間單位是 Unix 秒
 * - 不用 watch，所有 fetch 都是 action 內部 / 外部明確觸發
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import dayjs from 'dayjs'
import { fetchRecords as fetchRecordsApi } from '@/api/records'
import { toUserMessage } from '@/api/errorMessage'
import type { BackendRecord } from '@/api/records/types'
import { resolveGameLocalConfig } from '@/games/registry'
import { useI18n } from 'vue-i18n'

export type RangePreset = 'today' | 'last7' | 'custom'
export type HistoryStep = 'list' | 'selector' | 'custom'

// ============================================================================
// Helpers（module top-level）— 全部用 offsetMs 顯式傳入「目標顯示時區」，
// 不依賴瀏覽器 local tz。非台北用戶 / 跨時區伺服器都能正確對齊。
// ============================================================================

/**
 * 「目標時區的 Y/M/D 00:00:00」→ Unix 秒。
 *
 * 推導：UTC 同 Y/M/D 的 00:00:00 算出 ms，再扣 offsetMs，就是目標時區當日 00:00 的 UTC 時刻。
 * 例：(2026, 4, 15, +8) → Date.UTC = 2026-05-15 00:00 UTC → - 8h → 2026-05-14 16:00 UTC
 *      = 台北的 2026-05-15 00:00。
 */
const ymdToStartSec = (y: number, m0: number, d: number, offsetMs: number): number => {
  // dayjs.utc 以 UTC 視角構造該日（startOf('day') = 00:00:00.000），再扣 offsetMs
  // 取得目標時區當日 00:00 的 UTC instant。
  const utcMs = dayjs.utc().year(y).month(m0).date(d).startOf('day').valueOf()
  return Math.floor((utcMs - offsetMs) / 1000)
}

/** 同上但取該日 23:59:59.999 */
const ymdToEndSec = (y: number, m0: number, d: number, offsetMs: number): number => {
  const utcMs = dayjs.utc().year(y).month(m0).date(d).endOf('day').valueOf()
  return Math.floor((utcMs - offsetMs) / 1000)
}

/**
 * Unix 毫秒 → 目標時區的 { y, m0, d }（m0 = 0-based month 跟 Date API 對齊）。
 * 偽 UTC：把 instant 加 offsetMs 後用 getUTC* 讀就是「目標時區的本地年月日」。
 */
const instantToYmdInTz = (ms: number, offsetMs: number): { y: number; m0: number; d: number } => {
  // dayjs.utc(instant + offset) 等於「目標時區本地時刻 wrapped 在 UTC 視角」
  const shifted = dayjs.utc(ms + offsetMs)
  return {
    y: shifted.year(),
    m0: shifted.month(), // dayjs.month() 跟 Date 一樣是 0-indexed
    d: shifted.date(),
  }
}

/** Unix 秒 → 目標時區的 ['YYYY', 'MM', 'DD']（給 CustomRangePicker default 值用） */
const secToColumns = (sec: number, offsetMs: number): string[] => {
  const shifted = dayjs.utc(sec * 1000 + offsetMs)
  return [shifted.format('YYYY'), shifted.format('MM'), shifted.format('DD')]
}

// ============================================================================
// Store
// ============================================================================

export const useHistoryStore = defineStore('history', () => {
  const { t } = useI18n()

  // ── 顯示時區 ────────────────────────────────────────────────────
  // 整個歷史頁面（列表 / 詳情 / footer）的 GMT 偏移單一來源。
  // 2026-09 起改「用戶瀏覽器時區」（後端要求 query 帶用戶當地時間；後端存的 t
  // 是 UTC，前端加此 offset 顯示）。開頁快照一次，DST 切換中途不追。
  // 之後若要支援用戶手選時區，把 ref 改 mutable 並補 setter 即可。
  //
  // 用途：
  //   - fetchRecords 的 RFC3339 query（start/end + '±HH:MM'）→ 後端拿到用戶當地時間範圍
  //   - rangeLabel / footer 日期字串：把 Unix 秒以這個 offset 解讀
  //   - HistoryTable 欄頭 / DetailHeader 副標：顯示「(GMT+X:XX)」字串
  //   - Row 內 HH:MM:SS / MM/DD 用此 offset 做顯示
  //
  // getTimezoneOffset() 回「UTC − local」分鐘（GMT+8 = -480），取負轉小時；
  // 半時區（印度 +5.5 / 尼泊爾 +5.75）是小數，label / iso 的 mm 計算都吃得下。
  const timezoneOffsetHours = ref<number>(-new Date().getTimezoneOffset() / 60)
  /** 偏移毫秒，給 `new Date(utcSec * 1000 + offsetMs)` 取偽 UTC 用 */
  const timezoneOffsetMs = computed<number>(() => timezoneOffsetHours.value * 60 * 60 * 1000)
  /** 顯示字串：'GMT+8:00' / 'GMT-3:30' */
  const timezoneLabel = computed<string>(() => {
    const h = timezoneOffsetHours.value
    const sign = h >= 0 ? '+' : '-'
    const abs = Math.abs(h)
    const hh = Math.floor(abs)
    const mm = Math.round((abs - hh) * 60)
    return `GMT${sign}${hh}:${String(mm).padStart(2, '0')}`
  })
  /** RFC3339 拼接用：'+08:00' / '-03:30'（給 toRfc3339 帶入後端 query 字串） */
  const timezoneIso = computed<string>(() => {
    const h = timezoneOffsetHours.value
    const sign = h >= 0 ? '+' : '-'
    const abs = Math.abs(h)
    const hh = String(Math.floor(abs)).padStart(2, '0')
    const mm = String(Math.round((abs - Math.floor(abs)) * 60)).padStart(2, '0')
    return `${sign}${hh}:${mm}`
  })

  /**
   * Unix 秒 → 'YYYY/M/D'（依顯示時區）
   * 偽 UTC 寫法：dayjs.utc 在 UTC 視角讀 instant+offset 等於目標時區本地年月日。
   *
   * 注意：M 跟 D 是「無前導 0」格式（'2026/5/1' 不是 '2026/05/01'）— rangeLabel
   * 顯示需求；要前導 0 就改用 'YYYY/MM/DD'。
   */
  const formatDate = (sec: number): string => {
    return dayjs.utc(sec * 1000 + timezoneOffsetMs.value).format('YYYY/M/D')
  }

  // ── 時間範圍 / 面板 state ───────────────────────────────────────
  const preset = ref<RangePreset>('today')
  // 初始化用「目標時區的今天」00:00 ~ 23:59
  const _initToday = instantToYmdInTz(Date.now(), timezoneOffsetMs.value)
  const start_time = ref<number>(
    ymdToStartSec(_initToday.y, _initToday.m0, _initToday.d, timezoneOffsetMs.value),
  )
  const end_time = ref<number>(
    ymdToEndSec(_initToday.y, _initToday.m0, _initToday.d, timezoneOffsetMs.value),
  )
  const step = ref<HistoryStep>('list')

  // ── 紀錄列表 state ─────────────────────────────────────────────
  /** 目前的 gameId，由 HistoryTable mount 時 set 進來；gameCode 從 registry 派生 */
  const gameId = ref<string | null>(null)

  /** 紀錄列表（後端 raw shape，row 元件自己解析） */
  const records = ref<BackendRecord[]>([])
  /** 總筆數（後端 envelope.tt） */
  const total = ref<number>(0)
  /**
   * 累計總投注（footer 用）＝ 後端 envelope.**gtca**（累計消耗，買免遊局算購買價），
   * 與單筆列的 `ibf ? ca : ba` 同一口徑；後端沒回 gtca 才退回 gtba。
   */
  const totalBet = ref<number>(0)
  /** 累計總派彩（後端 envelope.gtwa；缺則前端 sum） */
  const totalWin = ref<number>(0)

  /** 首次載入中（page=1） */
  const loading = ref(false)
  /** 加載更多中（page>1） */
  const loadingMore = ref(false)
  const fetchError = ref<string | null>(null)
  const currentPage = ref(1)
  const pageSize = ref(20)

  /** 當前選中要顯示詳情的紀錄；null = 顯示列表 */
  const selectedRecord = ref<BackendRecord | null>(null)

  /**
   * Replay（對接商盤面回放）模式旗標。
   * true 時：HistoryDetailModal 跳過「開啟即重打列表」的 watch，並強制只顯示 detail
   * （replay 沒有列表 / 沒有登入 token，不能打 records 列表 API）。
   * 由 ResultDetailApp 在灌入單局 selectedRecord 前設 true。
   */
  const replayMode = ref<boolean>(false)
  const setReplayMode = (v: boolean): void => {
    replayMode.value = v
  }

  // ── Getters ────────────────────────────────────────────────────
  const rangeLabel = computed<string>(() => {
    switch (preset.value) {
      case 'today':
        return t('common.history.today')
      case 'last7':
        return t('common.history.last7days')
      case 'custom':
        return `${formatDate(start_time.value)} - ${formatDate(end_time.value)}`
    }
    // 三個 preset 已窮舉（RangePreset union，TS 保證走不到這裡）；
    // 這行只是滿足 vue/return-in-computed-property —— eslint 看不出 switch 是窮舉的。
    return ''
  })

  const startDateColumns = computed<string[]>(() =>
    secToColumns(start_time.value, timezoneOffsetMs.value),
  )
  const endDateColumns = computed<string[]>(() =>
    secToColumns(end_time.value, timezoneOffsetMs.value),
  )

  /** gameCode 從遊戲的 config.ts（registry）派生 */
  const gameCode = computed<string>(() => {
    if (!gameId.value) return ''
    const cfg = resolveGameLocalConfig(gameId.value)
    return cfg?.gameCode || ''
  })

  /** 還有下一頁可載入嗎 */
  const hasMore = computed<boolean>(() => records.value.length < total.value)

  // ── Fetch actions ──────────────────────────────────────────────

  /**
   * 設定當前 gameId（store 內部用 registry 派生 gameCode）
   * HistoryTable mount 時呼叫一次
   */
  const setGameId = (id: string | null): void => {
    gameId.value = id
  }

  /**
   * 取得歷史紀錄
   * @param page 預設 1；page=1 替換 records，page>1 追加
   */
  const fetchRecords = async (page: number = 1): Promise<void> => {
    if (!gameCode.value) {
      fetchError.value = '缺少 gameCode（請先 setGameId）'
      return
    }

    if (page === 1) {
      loading.value = true
    } else {
      loadingMore.value = true
    }
    fetchError.value = null

    // ── DEV only：假 record 注入（`localStorage.__DEV_HISTORY_RECORDS__` = BackendRecord[] JSON）──
    //    給「真機拿不到的極端盤面」用（例：三位數倍率、滿盤月亮）。`import.meta.env.DEV` 是 Vite
    //    build-time 常數 → 線上 bundle 整段 tree-shake 掉，不是 runtime env 開關。
    //    用法：DevTools console → localStorage.setItem('__DEV_HISTORY_RECORDS__', JSON.stringify([...]))
    //    再開 history modal；清掉：removeItem。
    if (import.meta.env.DEV) {
      const raw = localStorage.getItem('__DEV_HISTORY_RECORDS__')
      if (raw) {
        try {
          const list = JSON.parse(raw) as BackendRecord[]
          if (Array.isArray(list)) {
            console.warn('[web][history] DEV：使用 localStorage 假 record', list.length, '筆')
            records.value = list
            total.value = list.length
            totalBet.value = 0
            totalWin.value = 0
            currentPage.value = 1
            loading.value = false
            loadingMore.value = false
            return
          }
        } catch (e) {
          console.warn('[web][history] DEV 假 record JSON 解析失敗，改打 API', e)
        }
      }
    }

    try {
      const result = await fetchRecordsApi({
        gameCode: gameCode.value,
        page,
        size: pageSize.value,
        start_time: start_time.value,
        end_time: end_time.value,
        // 把顯示時區一起送進去，讓 RFC3339 拼字串用同一個 offset
        // 而不是吃瀏覽器本地 tz（非台北用戶會送錯時間）
        timezoneOffsetMs: timezoneOffsetMs.value,
        timezoneIso: timezoneIso.value,
      })

      console.log('[web] result', result)

      const list = Array.isArray(result?.l) ? result.l : []
      if (page === 1) {
        records.value = list
      } else {
        records.value = [...records.value, ...list]
      }
      total.value = result?.tt ?? 0
      // gtca = 累計消耗（買免遊算購買價），跟列表每列的 resolveBetAmount 口徑一致；
      // 後端漏回 gtca 時才退回純投注 gtba
      totalBet.value = result?.gtca ?? result?.gtba ?? 0
      totalWin.value = result?.gtwa ?? 0
      currentPage.value = page
    } catch (e) {
      // ⚠️ 不可用 e.message —— ApiError 的 message 是 'Request timed out' / 'HTTP 502'
      //    這類給工程師看的字串，會直接顯示在歷史頁上。
      fetchError.value = toUserMessage(e)
    } finally {
      loading.value = false
      loadingMore.value = false
    }
  }

  /** 載入下一頁（觸底用） */
  const loadMore = async (): Promise<void> => {
    if (loading.value || loadingMore.value || !hasMore.value) return
    await fetchRecords(currentPage.value + 1)
  }

  // ── 面板 actions（操作後自動重抓 page=1） ─────────────────────

  const selectToday = (): void => {
    preset.value = 'today'
    // 「今天 in 目標時區」的 Y/M/D，再算回 Unix 秒
    const { y, m0, d } = instantToYmdInTz(Date.now(), timezoneOffsetMs.value)
    start_time.value = ymdToStartSec(y, m0, d, timezoneOffsetMs.value)
    end_time.value = ymdToEndSec(y, m0, d, timezoneOffsetMs.value)
    step.value = 'list'
    void fetchRecords(1)
  }

  const selectLast7Days = (): void => {
    preset.value = 'last7'
    // 「今天 in 目標時區」的 Y/M/D，往前推 6 天當 start。
    // 用 d - 6 直接傳給 Date.UTC，會自動處理跨月跨年（例 5/3 - 6 = 4/27）。
    const { y, m0, d } = instantToYmdInTz(Date.now(), timezoneOffsetMs.value)
    start_time.value = ymdToStartSec(y, m0, d - 6, timezoneOffsetMs.value)
    end_time.value = ymdToEndSec(y, m0, d, timezoneOffsetMs.value)
    step.value = 'list'
    void fetchRecords(1)
  }

  /**
   * 自定日期範圍。Date 物件的 getFullYear / getMonth / getDate 是使用者在
   * picker 上選的數字（CustomRangePickerPanel 用 `new Date(y, m-1, d)` 構造），
   * 直接當「目標顯示時區的年月日」處理 → 不依賴瀏覽器 local tz。
   */
  const selectCustomRange = (startDate: Date, endDate: Date): void => {
    preset.value = 'custom'
    let s = startDate
    let e = endDate
    if (s.getTime() > e.getTime()) {
      ;[s, e] = [e, s]
    }
    start_time.value = ymdToStartSec(
      s.getFullYear(),
      s.getMonth(),
      s.getDate(),
      timezoneOffsetMs.value,
    )
    end_time.value = ymdToEndSec(e.getFullYear(), e.getMonth(), e.getDate(), timezoneOffsetMs.value)
    step.value = 'list'
    void fetchRecords(1)
  }

  const openSelector = (): void => {
    step.value = 'selector'
  }

  const openCustom = (): void => {
    step.value = 'custom'
  }

  const backToSelector = (): void => {
    step.value = 'selector'
  }

  const backToList = (): void => {
    step.value = 'list'
  }

  /** 設定 / 清除 selectedRecord（row click 開詳情、← back 關詳情） */
  const setSelectedRecord = (r: BackendRecord | null): void => {
    selectedRecord.value = r
  }

  /** modal 關閉時清狀態（保留 gameId，避免下次再 set 一遍） */
  const reset = (): void => {
    step.value = 'list'
    records.value = []
    total.value = 0
    totalBet.value = 0
    totalWin.value = 0
    currentPage.value = 1
    fetchError.value = null
    loading.value = false
    loadingMore.value = false
    selectedRecord.value = null
  }

  return {
    // 顯示時區
    timezoneOffsetHours,
    timezoneOffsetMs,
    timezoneLabel,
    timezoneIso,
    // 時間 / 面板 state
    preset,
    start_time,
    end_time,
    step,
    // 紀錄 state
    gameId,
    records,
    total,
    totalBet,
    totalWin,
    loading,
    loadingMore,
    fetchError,
    currentPage,
    pageSize,
    selectedRecord,
    replayMode,
    // getters
    rangeLabel,
    startDateColumns,
    endDateColumns,
    gameCode,
    hasMore,
    // actions
    setGameId,
    setReplayMode,
    fetchRecords,
    loadMore,
    selectToday,
    selectLast7Days,
    selectCustomRange,
    setSelectedRecord,
    openSelector,
    openCustom,
    backToSelector,
    backToList,
    reset,
  }
})
